import { useEffect, useState } from 'react';
import { api, ErreurApi, type PlanTravail, type Tuile } from './api';

/**
 * LE PLAN DE TRAVAIL — §37 à §43.
 *
 * Ce qu'un mode métier ajoute par rapport à un simple filtrage du rail : la
 * réponse à « qu'est-ce qui m'attend ? ». Sans lui, un géomètre qui se connecte
 * arrive sur une carte nationale et doit CHERCHER son propre travail.
 *
 * ⚠️ CHAQUE TUILE MONTRE SA POPULATION, TOUJOURS. « 0 levé à décider » ne veut
 * rien dire sans « sur combien ». C'est la même règle que le contrôle de
 * qualité du §8, qui affiche `candidats_examines` : la différence entre un
 * compte rassurant et un compte qui n'a rien eu à regarder. Le service rend les
 * deux nombres ; l'écran n'a pas le droit d'en cacher un.
 *
 * ⚠️ ET « MASQUÉ » N'EST PAS « ZÉRO ». Une tuile que l'habilitation ferme
 * affiche un cadenas et son motif, jamais un 0. Un 0 se lit « il n'y a rien » ;
 * la vérité est « vous n'avez pas le droit de savoir s'il y a quelque chose ».
 */

const APPARENCE: Record<Tuile['etat'], { classe: string; etiquette: string }> = {
  a_traiter:       { classe: 'tuile-agir',    etiquette: 'à traiter' },
  rien_en_attente: { classe: 'tuile-calme',   etiquette: 'rien en attente' },
  sans_matiere:    { classe: 'tuile-vide',    etiquette: 'sans matière' },
  masque:          { classe: 'tuile-masque',  etiquette: 'masqué' },
  non_implemente:  { classe: 'tuile-erreur',  etiquette: 'non implémenté' },
};

const MENTION_PORTEE: Record<string, string> = {
  nationale: 'territoire entier',
  divisions: 'limité à vos divisions',
  non_scopable: 'non limitable à une division',
};

/**
 * ⚠️ `encastre` — LA FUSION DEMANDÉE PAR LE FONDATEUR, LE 21/09/2026 :
 * « bureau et plan de travail doivent être fusionnés ».
 *
 * ⚠️ ET ELLE EST LOCALE AU NOTAIRE, parce que la mesure l'impose :
 *      bureau → notaire                                     (1 mode)
 *      plan   → collectivite etat fiscal geometre
 *               investisseur notaire urbanisme              (7 modes)
 * `plan` est le point d'ATTERRISSAGE des six autres. Le retirer du catalogue,
 * ou y rediriger globalement, laisserait le géomètre, le fiscaliste,
 * l'urbaniste, la collectivité, l'investisseur et l'État sans écran d'arrivée.
 * On fusionne donc l'AFFICHAGE chez le notaire, pas le composant.
 *
 * ⚠️ ET ON RÉUTILISE CE COMPOSANT PLUTÔT QUE DE RECOPIER SES TUILES. Deux
 * implémentations d'une même règle divergent au premier correctif appliqué à
 * une seule — la règle du dépôt, payée assez de fois ailleurs.
 */
export function PlanDeTravail({
  modeCode, onOuvrirRubrique, encastre,
}: {
  modeCode: string;
  onOuvrirRubrique: (rubrique: string) => void;
  /** Rendu DANS le bureau : pas d'en-tête de page, un titre de section. */
  encastre?: boolean;
}) {
  const [plan, setPlan] = useState<PlanTravail | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);

  useEffect(() => {
    let vivant = true;
    setPlan(null); setErreur(null);
    api.modes.plan(modeCode)
      .then((p) => { if (vivant) setPlan(p); })
      .catch((e: unknown) => {
        if (!vivant) return;
        setErreur(e instanceof ErreurApi ? e.message : String(e));
      });
    return () => { vivant = false; };
  }, [modeCode]);

  if (erreur) {
    return (
      <div className="a-batir">
        {encastre ? <h3>Ce qui vous attend</h3> : <h2>Plan de travail</h2>}
        <p className="erreur">{erreur}</p>
        <p className="mention">
          Le plan n'a pas pu être mesuré. Ce n'est pas « rien à faire » : c'est
          « on n'a pas pu regarder ».
        </p>
      </div>
    );
  }
  if (!plan) return <div className="attente-plein">Mesure du plan de travail…</div>;

  const agir = plan.tuiles.filter((t) => t.etat === 'a_traiter').length;

  return (
    <div className={encastre ? 'plan encastre' : 'plan'}>
      <header className="plan-tete">
        <div>
          {/* ⚠️ ENCASTRÉ, LE PLAN N'EST PLUS UNE PAGE : il devient une section
              du bureau, et son titre doit descendre d'un cran. Deux <h2> sur
              un même écran ne disent plus lequel est le sujet — et un lecteur
              d'écran annonce deux titres de même niveau. */}
          {encastre
            ? <h3>Ce qui vous attend <span className="plan-section">{plan.mode.section}</span></h3>
            : <h2>{plan.mode.libelle} <span className="plan-section">{plan.mode.section}</span></h2>}
          <p className="mention">{plan.mode.resume}</p>
        </div>
        <div className="plan-portee">
          <strong>
            {agir === 0
              ? 'Rien n’attend une décision de votre part'
              : `${agir} sujet${agir > 1 ? 's' : ''} appelle${agir > 1 ? 'nt' : ''} une action`}
          </strong>
          <span>
            {plan.portee.type === 'nationale'
              ? 'Portée nationale'
              : `Portée : ${plan.portee.divisions} division(s)`}
            {' · '}mesuré à {new Date(plan.mesure_le).toLocaleTimeString('fr-FR')}
          </span>
        </div>
      </header>

      <div className="tuiles">
        {plan.tuiles.map((t) => {
          const a = APPARENCE[t.etat];
          return (
            <article key={t.compteur} className={`tuile ${a.classe}`}>
              <div className="tuile-haut">
                <span className="tuile-etiquette">{a.etiquette}</span>
                {t.portee && <span className="tuile-portee">{MENTION_PORTEE[t.portee]}</span>}
              </div>

              <div className="tuile-nombre">
                {t.etat === 'masque' || t.etat === 'non_implemente'
                  ? <span className="tuile-cadenas" aria-label="indisponible">—</span>
                  : <strong>{t.valeur}</strong>}
                {/* La population accompagne TOUJOURS la valeur : un compte sans
                    son ensemble de référence n'est pas une mesure. */}
                {t.population !== null && (
                  <em>sur {t.population.toLocaleString('fr-FR')}</em>
                )}
              </div>

              <h3>{t.libelle}</h3>
              <p>{t.etat === 'sans_matiere'
                ? "La source existe et ne contient encore rien. Ce n'est pas « tout est en règle » : il n'y a rien à examiner."
                : t.aide}</p>

              {t.motif && <p className="tuile-motif">{t.motif}</p>}

              {t.rubrique && t.etat === 'a_traiter' && (
                <button className="bouton discret" onClick={() => onOuvrirRubrique(t.rubrique!)}>
                  Ouvrir
                </button>
              )}
            </article>
          );
        })}
      </div>

      <p className="mention plan-pied">
        Un mode n'accorde aucun droit : il choisit ce qui est montré en premier.
        Ce que vous pouvez lire reste décidé par vos rôles, et revérifié par le
        service à chaque requête.
      </p>
    </div>
  );
}
